// AssoFormules.jsx — Gestion des formules d'adhésion + codes promo (président only)
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Plus, X, Trash2, Edit3, Loader2, BadgePercent, Gift, Calendar, ToggleLeft, ToggleRight, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const PERIODES = [
  { value: 'unique', label: 'Unique' },
  { value: 'reunion', label: 'À la réunion' },
  { value: 'mensuel', label: 'Mensuel' },
  { value: 'trimestriel', label: 'Trimestriel' },
  { value: 'annuel', label: 'Annuel' },
];

export default function AssoFormules() {
  const [tab, setTab] = useState('formules');
  const [formules, setFormules] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // formule object or 'new'
  const [editingCode, setEditingCode] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [f, c] = await Promise.all([
        api.get('/formules'),
        api.get('/formules/codes-promo/all').catch(() => ({ data: [] })),
      ]);
      setFormules(f.data);
      setCodes(c.data);
    } catch (err) { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const deleteFormule = async (id) => {
    if (!window.confirm('Supprimer cette formule ?')) return;
    try {
      await api.delete(`/formules/${id}`);
      toast.success('Formule supprimée');
      load();
    } catch (err) { toast.error('Erreur'); }
  };

  const toggleFormule = async (f) => {
    try {
      await api.put(`/formules/${f.id}`, { actif: !f.actif });
      load();
    } catch { toast.error('Erreur'); }
  };

  const deleteCode = async (id) => {
    if (!window.confirm('Supprimer ce code ?')) return;
    try {
      await api.delete(`/formules/codes-promo/${id}`);
      toast.success('Code supprimé');
      load();
    } catch { toast.error('Erreur'); }
  };

  const toggleCode = async (c) => {
    try {
      await api.patch(`/formules/codes-promo/${c.id}`, { actif: !c.actif });
      load();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-6xl mx-auto" data-testid="asso-formules">

      {/* En-tête éditorial */}
      <div className="mb-8 slide-up">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--theme-primary)' }}>
          Adhésions
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0F172A] leading-[1.02] mb-3">
          Formules &amp; <span className="italic font-medium" style={{ color: 'var(--theme-primary)' }}>codes promo.</span>
        </h1>
        <p className="text-base text-[#475569] max-w-2xl">
          Configurez vos tarifs (gratuit, à la réunion, annuel…) et créez des codes promotionnels pour vos campagnes d'acquisition.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#E2E8F0] mb-8" data-testid="formules-tabs">
        <TabButton active={tab === 'formules'} onClick={() => setTab('formules')} count={formules.length} testid="tab-formules">
          Formules
        </TabButton>
        <TabButton active={tab === 'codes'} onClick={() => setTab('codes')} count={codes.length} testid="tab-codes">
          Codes promo
        </TabButton>
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>
      ) : tab === 'formules' ? (
        <>
          <div className="flex justify-end mb-5">
            <button onClick={() => setEditing('new')} className="btn-primary" data-testid="add-formule-btn">
              <Plus className="w-4 h-4" /> Ajouter une formule
            </button>
          </div>
          {formules.length === 0 ? (
            <EmptyState icon={Gift} title="Aucune formule" desc="Créez votre première formule d'adhésion pour permettre aux membres de s'inscrire." />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger" data-testid="formules-grid">
              {formules.map((f) => <FormuleCard key={f.id} f={f} onEdit={() => setEditing(f)} onDelete={() => deleteFormule(f.id)} onToggle={() => toggleFormule(f)} />)}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex justify-end mb-5">
            <button onClick={() => setEditingCode('new')} className="btn-primary" data-testid="add-code-btn">
              <Plus className="w-4 h-4" /> Créer un code
            </button>
          </div>
          {codes.length === 0 ? (
            <EmptyState icon={BadgePercent} title="Aucun code promo" desc="Créez des codes promotionnels (-20%, -10€, gratuit) pour vos campagnes." />
          ) : (
            <div className="space-y-3" data-testid="codes-list">
              {codes.map((c) => <CodeCard key={c.id} c={c} formules={formules} onDelete={() => deleteCode(c.id)} onToggle={() => toggleCode(c)} />)}
            </div>
          )}
        </>
      )}

      {editing && (
        <FormuleModal formule={editing === 'new' ? null : editing} onClose={(saved) => { setEditing(null); if (saved) load(); }} />
      )}
      {editingCode && (
        <CodeModal code={editingCode === 'new' ? null : editingCode} formules={formules} onClose={(saved) => { setEditingCode(null); if (saved) load(); }} />
      )}
    </div>
  );
}

function TabButton({ active, onClick, count, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`relative px-5 py-3 text-sm font-semibold transition-colors ${active ? 'text-[#0F172A]' : 'text-[#94A3B8] hover:text-[#475569]'}`}
    >
      {children}
      <span className={`ml-2 text-[10px] font-black px-2 py-0.5 rounded-full ${active ? 'bg-[var(--theme-primary)] text-white' : 'bg-[#F1F5F9] text-[#94A3B8]'}`}>
        {count}
      </span>
      {active && <span className="absolute -bottom-px inset-x-0 h-0.5 bg-[var(--theme-primary)]" />}
    </button>
  );
}

function FormuleCard({ f, onEdit, onDelete, onToggle }) {
  return (
    <div className={`bg-white border rounded-3xl p-6 relative transition-all hover:shadow-md hover:-translate-y-0.5 ${f.actif ? 'border-[#E2E8F0]' : 'border-[#F1F5F9] opacity-60'}`} data-testid={`formule-card-${f.id}`}>
      {f.gratuit_premiere && (
        <div className="absolute -top-2.5 left-5 px-2 py-0.5 rounded-full bg-[#10B981] text-white text-[9px] font-black uppercase tracking-[0.16em]">1ère offerte</div>
      )}
      <div className="flex items-start justify-between mb-4">
        <h3 className="font-display text-xl font-bold text-[#0F172A]">{f.nom}</h3>
        <button onClick={onToggle} className="text-[#94A3B8] hover:text-[#0F172A]" title={f.actif ? 'Désactiver' : 'Activer'}>
          {f.actif ? <ToggleRight className="w-5 h-5 text-[var(--theme-primary)]" /> : <ToggleLeft className="w-5 h-5" />}
        </button>
      </div>
      <div className="font-display text-4xl font-black tracking-tight mb-1" style={{ color: 'var(--theme-primary)' }}>
        {f.prix_cts === 0 ? 'Gratuit' : `${(f.prix_cts / 100).toFixed(0)}€`}
        {f.periode_label && <span className="text-base font-normal text-[#475569] ml-1">{f.periode_label}</span>}
      </div>
      {f.description && <p className="text-sm text-[#475569] mt-3 line-clamp-3">{f.description}</p>}
      <div className="flex gap-2 mt-5 pt-4 border-t border-[#F1F5F9]">
        <button onClick={onEdit} className="btn-ghost !text-xs" data-testid={`edit-formule-${f.id}`}><Edit3 className="w-3.5 h-3.5" /> Modifier</button>
        <button onClick={onDelete} className="btn-ghost !text-xs text-[#DC2626] hover:bg-[#FEF2F2]" data-testid={`delete-formule-${f.id}`}><Trash2 className="w-3.5 h-3.5" /> Supprimer</button>
      </div>
    </div>
  );
}

function CodeCard({ c, formules, onDelete, onToggle }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(c.code); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const labelType = c.type === 'pourcentage' ? `-${c.valeur}%` : c.type === 'montant' ? `-${(c.valeur / 100).toFixed(0)}€` : 'Gratuit';
  return (
    <div className={`bg-white border rounded-2xl p-5 flex items-center gap-4 ${c.actif ? 'border-[#E2E8F0]' : 'border-[#F1F5F9] opacity-60'}`} data-testid={`code-card-${c.id}`}>
      <div className="w-12 h-12 rounded-xl grid place-items-center shrink-0" style={{ background: 'var(--theme-primary-soft)', color: 'var(--theme-primary)' }}>
        <BadgePercent className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <button onClick={copy} className="font-mono font-bold text-[#0F172A] hover:text-[var(--theme-primary)] transition-colors flex items-center gap-1" title="Copier" data-testid={`copy-code-${c.id}`}>
            {c.code} {copied ? <Check className="w-3 h-3 text-[#10B981]" /> : <Copy className="w-3 h-3 opacity-50" />}
          </button>
          <span className="badge badge-primary">{labelType}</span>
          {c.usage_max && <span className="text-[11px] text-[#94A3B8]">{c.usage_count}/{c.usage_max} utilisations</span>}
          {c.valid_until && <span className="text-[11px] text-[#94A3B8]">Jusqu'au {new Date(c.valid_until).toLocaleDateString('fr-FR')}</span>}
        </div>
        {c.description && <p className="text-sm text-[#475569] truncate">{c.description}</p>}
        {c.formules_eligibles?.length > 0 && (
          <div className="text-xs text-[#94A3B8] mt-1 truncate">
            Pour : {c.formules_eligibles.map((id) => formules.find((f) => f.id === id)?.nom).filter(Boolean).join(', ')}
          </div>
        )}
      </div>
      <button onClick={onToggle} className="text-[#94A3B8] hover:text-[#0F172A] p-1" title={c.actif ? 'Désactiver' : 'Activer'}>
        {c.actif ? <ToggleRight className="w-5 h-5 text-[var(--theme-primary)]" /> : <ToggleLeft className="w-5 h-5" />}
      </button>
      <button onClick={onDelete} className="text-[#94A3B8] hover:text-[#DC2626] p-1" title="Supprimer" data-testid={`delete-code-${c.id}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

function EmptyState({ icon: Ic, title, desc }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-3xl p-12 text-center">
      <div className="w-14 h-14 rounded-2xl bg-[#F1F5F9] grid place-items-center mx-auto mb-4">
        <Ic className="w-6 h-6 text-[#94A3B8]" />
      </div>
      <h3 className="font-display text-xl font-bold text-[#0F172A] mb-2">{title}</h3>
      <p className="text-sm text-[#475569] max-w-md mx-auto">{desc}</p>
    </div>
  );
}

function FormuleModal({ formule, onClose }) {
  const [form, setForm] = useState(formule || {
    nom: '', description: '', prix_cts: 0, periode: 'unique', periode_label: '',
    conditions: '', gratuit_premiere: false, max_reunions: null, actif: true, ordre: 100,
  });
  const [saving, setSaving] = useState(false);
  const isNew = !formule;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) await api.post('/formules', form);
      else await api.put(`/formules/${formule.id}`, form);
      toast.success(isNew ? 'Formule créée' : 'Formule mise à jour');
      onClose(true);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-md grid place-items-center p-4 fade-in" onClick={() => onClose(false)}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl p-7 max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="formule-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-2xl font-bold text-[#0F172A]">{isNew ? 'Nouvelle formule' : 'Modifier'}</h3>
          <button type="button" onClick={() => onClose(false)} className="text-[#94A3B8] hover:text-[#0F172A]"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <Field label="Nom *">
            <input required type="text" className="ap-input" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Ex: Adhésion annuelle" data-testid="formule-nom" />
          </Field>
          <Field label="Description">
            <textarea className="ap-input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Détail visible par les inscrits" data-testid="formule-desc" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Prix (€)">
              <input type="number" min="0" step="0.01" className="ap-input" value={(form.prix_cts / 100).toString()} onChange={(e) => setForm({ ...form, prix_cts: Math.round(parseFloat(e.target.value || 0) * 100) })} data-testid="formule-prix" />
            </Field>
            <Field label="Période">
              <select className="ap-input" value={form.periode} onChange={(e) => setForm({ ...form, periode: e.target.value })} data-testid="formule-periode">
                {PERIODES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Libellé personnalisé (optionnel)">
            <input type="text" className="ap-input" value={form.periode_label} onChange={(e) => setForm({ ...form, periode_label: e.target.value })} placeholder="ex: /an, /réunion, ou 3× 50€" />
          </Field>
          <Field label="Conditions / restrictions">
            <input type="text" className="ap-input" value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} placeholder="ex: Réservé aux nouveaux membres" />
          </Field>
          <div className="flex items-center gap-3 p-3 rounded-xl bg-[#F9F9F8] border border-[#E2E8F0]">
            <input type="checkbox" checked={form.gratuit_premiere} onChange={(e) => setForm({ ...form, gratuit_premiere: e.target.checked })} id="gp" className="w-4 h-4" data-testid="formule-gp" />
            <label htmlFor="gp" className="text-sm font-medium text-[#0F172A] cursor-pointer">Formule "1ère visite offerte" (auto pour nouveaux)</label>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-xl bg-[#F9F9F8] border border-[#E2E8F0]">
            <input type="checkbox" checked={form.actif} onChange={(e) => setForm({ ...form, actif: e.target.checked })} id="ac" className="w-4 h-4" />
            <label htmlFor="ac" className="text-sm font-medium text-[#0F172A] cursor-pointer">Active (visible publiquement)</label>
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-6 pt-5 border-t border-[#F1F5F9]">
          <button type="button" onClick={() => onClose(false)} className="btn-secondary">Annuler</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="formule-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isNew ? 'Créer' : 'Enregistrer')}</button>
        </div>
      </form>
    </div>
  );
}

function CodeModal({ code, formules, onClose }) {
  const [form, setForm] = useState(code || {
    code: '', type: 'pourcentage', valeur: 10, description: '',
    formules_eligibles: [], valid_until: '', usage_max: '',
  });
  const [saving, setSaving] = useState(false);
  const isNew = !code;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.valid_until) delete payload.valid_until;
      if (!payload.usage_max) delete payload.usage_max;
      if (isNew) await api.post('/formules/codes-promo', payload);
      else await api.patch(`/formules/codes-promo/${code.id}`, payload);
      toast.success(isNew ? 'Code créé' : 'Code mis à jour');
      onClose(true);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  const toggleFormule = (id) => {
    setForm((f) => {
      const has = f.formules_eligibles?.includes(id);
      return { ...f, formules_eligibles: has ? f.formules_eligibles.filter((x) => x !== id) : [...(f.formules_eligibles || []), id] };
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-md grid place-items-center p-4 fade-in" onClick={() => onClose(false)}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl p-7 max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="code-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-2xl font-bold text-[#0F172A]">{isNew ? 'Nouveau code' : 'Modifier'}</h3>
          <button type="button" onClick={() => onClose(false)} className="text-[#94A3B8] hover:text-[#0F172A]"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          {isNew && (
            <Field label="Code *">
              <input required type="text" className="ap-input font-mono uppercase" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="BIENVENUE2026" data-testid="code-input" />
            </Field>
          )}
          <Field label="Type de réduction">
            <select className="ap-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} disabled={!isNew} data-testid="code-type">
              <option value="pourcentage">Pourcentage (-X%)</option>
              <option value="montant">Montant fixe (-X€)</option>
              <option value="gratuit">Gratuit (100% remise)</option>
            </select>
          </Field>
          {form.type !== 'gratuit' && (
            <Field label={form.type === 'pourcentage' ? 'Pourcentage (1-100)' : 'Montant (€)'}>
              <input type="number" min="1" step={form.type === 'pourcentage' ? '1' : '0.01'} required className="ap-input"
                value={form.type === 'pourcentage' ? form.valeur : (form.valeur / 100).toString()}
                onChange={(e) => setForm({ ...form, valeur: form.type === 'pourcentage' ? parseInt(e.target.value || 0, 10) : Math.round(parseFloat(e.target.value || 0) * 100) })}
                disabled={!isNew} data-testid="code-valeur"
              />
            </Field>
          )}
          <Field label="Description (visible par l'utilisateur)">
            <input type="text" className="ap-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="-20% sur l'adhésion annuelle" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Validité jusqu'au">
              <input type="date" className="ap-input" value={form.valid_until?.slice(0, 10) || ''} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
            </Field>
            <Field label="Usage max (vide = illimité)">
              <input type="number" min="1" className="ap-input" value={form.usage_max || ''} onChange={(e) => setForm({ ...form, usage_max: e.target.value })} />
            </Field>
          </div>
          {formules.length > 0 && (
            <Field label="Formules éligibles (vide = toutes)">
              <div className="space-y-1.5">
                {formules.map((f) => (
                  <label key={f.id} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#F9F9F8] cursor-pointer">
                    <input type="checkbox" checked={form.formules_eligibles?.includes(f.id) || false} onChange={() => toggleFormule(f.id)} className="w-4 h-4" />
                    <span className="text-sm">{f.nom} <span className="text-[#94A3B8]">— {f.prix_cts === 0 ? 'Gratuit' : `${(f.prix_cts / 100).toFixed(0)}€`}</span></span>
                  </label>
                ))}
              </div>
            </Field>
          )}
        </div>
        <div className="flex gap-2 justify-end mt-6 pt-5 border-t border-[#F1F5F9]">
          <button type="button" onClick={() => onClose(false)} className="btn-secondary">Annuler</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="code-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isNew ? 'Créer' : 'Enregistrer')}</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">{label}</label>
      {children}
    </div>
  );
}
